import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import MediumTypography from '../../components/formlib/MediumTypography';
import { FC, useEffect, useState } from 'react';
import { getPayrollHistory } from '../../services/configApi/activitySheet/weeklyActivitySheetProvider';
import { useIntl } from 'react-intl';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import { ResponseType } from '../../utils/type';
import { convertUTCtoLocalTimezone } from '../../utils/dateUtil';
import { ReactComponent as CloseIcon } from '../../assets/images/x.svg';
import {
  LOCAL_DATE_TIME_FORMAT,
  LOCAL_DATE_TIME_UTC_FORMAT,
} from '../../services/Constant';

export interface PayrollHistory {
  open: boolean;
  date: string;
  field: string;
  employeeId: string;
  titleId: string;
  defaultMessage: string;
}

export interface PayrollHistoryProps extends PayrollHistory {
  handleClose: () => void;
}

interface PayrollHistoryHeader {
  oldValue: string;
  newValue: string;
  modifiedBy: string;
  modifiedOn: string;
}

export interface PayrollHistoryResponse {
  oldValue: string;
  newValue: string;
  modifiedBy: string;
  modifiedOn: string;
}

const tableHeadCells: HeadCell<PayrollHistoryHeader>[] = [
  {
    id: 'oldValue',
    labelId: 'previousValueText',
    defaultLabelId: 'Old Value',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'newValue',
    labelId: 'currentValueText',
    defaultLabelId: 'New Value',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'modifiedBy',
    labelId: 'modifiedByText',
    defaultLabelId: 'Modified By',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'modifiedOn',
    labelId: 'modifiedOnText',
    defaultLabelId: 'Modified On',
    numeric: false,
    requiredSorting: false,
  },
];

const formatDecimalHours = (value: string | null): string => {
  if (value === null) {
    return '0.00';
  }
  const num = Number(value);
  return Number.isNaN(num) ? value : num.toFixed(2);
};

const PayrollHistoryDialog: FC<PayrollHistoryProps> = ({
  open,
  handleClose,
  employeeId,
  field,
  date,
  titleId,
  defaultMessage,
}) => {
  const intl = useIntl();
  const [history, setHistory] = useState<PayrollHistoryResponse[] | null>(null);
  const [successOrError, setSuccessOrError] = useState<ResponseType>('success');
  const [toastrId, setToastrId] = useState<string>();
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');

  useEffect(() => {
    if (open) {
      getPayrollHistory(employeeId, field, date)
        .then((response) => {
          setHistory(response);
        })
        .catch((error) => {
          setHistory(null);
          setSuccessOrError('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else if (error.code === 1172) {
            setToastrId('overlapInsuranceMessage');
            setToastrDefaultMessage('Failed to retrieve payroll history.');
          } else {
            setToastrId('SomethingWentWrongText');
            setToastrDefaultMessage('Something went wrong, Please try again!');
          }
        });
    }
  }, [open, employeeId, field, date]);

  return (
    <>
      {toastrId && (
        <SnackBarComponent
          open={toastrId !== undefined}
          handleClose={() => {
            setToastrId(undefined);
          }}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
          successOrError={successOrError}
        />
      )}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth={'md'}>
        <DialogTitle>
          <Grid container direction={'column'} rowSpacing={4}>
            <Grid
              item
              display={'flex'}
              justifyContent={'space-between'}
              className="mt-sm mb-sm"
            >
              <MediumTypography
                className="mainText-lg"
                label={intl.formatMessage({
                  id: titleId,
                  defaultMessage: defaultMessage,
                })}
              />
              <MediumTypography
                className="mainText-md ml-sm"
                label={'- ' + date}
                sxProps={{ flex: 1 }}
              />
              <CloseIcon onClick={handleClose} className="enablePopupIcon" />
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHeader
                className="listDataTableHead"
                labelSxProp={{ whiteSpace: 'normal' }}
                headerNames={tableHeadCells}
                checkBoxRequired={false}
              />
              <TableBody className="tableRowcss">
                {history?.map((row) => (
                  <TableRow key={row.oldValue}>
                    {field === 'lunch_time_hours' ? (
                      <TableCell>
                        {row.oldValue === 'true' ? 'Yes' : 'No'}
                      </TableCell>
                    ) : (
                      <TableCell>{formatDecimalHours(row.oldValue)}</TableCell>
                    )}
                    {field === 'lunch_time_hours' ? (
                      <TableCell>
                        {row.newValue === 'true' ? 'Yes' : 'No'}
                      </TableCell>
                    ) : (
                      <TableCell>{formatDecimalHours(row.newValue)}</TableCell>
                    )}
                    <TableCell>{row.modifiedBy}</TableCell>
                    <TableCell>
                      {convertUTCtoLocalTimezone(
                        row.modifiedOn,
                        LOCAL_DATE_TIME_UTC_FORMAT,
                        LOCAL_DATE_TIME_FORMAT,
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {history?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <MediumTypography
                        className="flex__ justifyContent-Center"
                        labelid="noHistoryRecordsFoundMessage"
                        defaultlabel="History will be available once the Activity Sheet is submitted for review."
                      />
                    </TableCell>
                  </TableRow>
                )}
                {history === null && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <MediumTypography
                        className="flex__ justifyContent-Center"
                        labelid="loadingText"
                        defaultlabel="Loading..."
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PayrollHistoryDialog;
